/**
 * The audio track of an MP4 or MOV file (ISO base media / QuickTime), read
 * without downloading the video.
 *
 * The container's index says where every audio sample sits in the file: the
 * `moov` box (wherever it is) for an ordinary file, or the `moof` boxes of a
 * fragmented one. Only the index and then the audio bytes are read, so ten
 * minutes of a camera file's sound costs a few hundred small reads rather than
 * a multi-gigabyte download.
 *
 * Times are presentation times in seconds, the way a player shows the file:
 * the edit list is applied, so an AAC track's encoder priming (edit media
 * time 1024 or 2112) and a delayed audio start (an empty edit) both line up.
 *
 * Supported audio: AAC and MP3 (decoded by the browser, see audioDecode.ts)
 * and uncompressed PCM (QuickTime twos/sowt/in24/in32/fl32/fl64/raw/lpcm and
 * ISO ipcm/fpcm).
 */

import type { ByteSource } from './byteSource';

export type MediaReadErrorCode = 'not-mp4' | 'no-audio' | 'unsupported-audio' | 'corrupt';

export class MediaReadError extends Error {
  constructor(readonly code: MediaReadErrorCode, message: string) {
    super(message);
    this.name = 'MediaReadError';
  }
}

export interface AacCodec {
  kind: 'aac';
  /** The core audio object type written into ADTS headers (1-4; 2 is AAC-LC). */
  objectType: number;
  /** Index into the AAC sampling-frequency table, for ADTS headers. */
  samplingIndex: number;
  channelConfig: number;
  sampleRate: number;
  channels: number;
}

export interface Mp3Codec {
  kind: 'mp3';
  sampleRate: number;
  channels: number;
}

export interface PcmCodec {
  kind: 'pcm';
  bits: 8 | 16 | 24 | 32 | 64;
  float: boolean;
  littleEndian: boolean;
  /** 8-bit QuickTime 'raw ' audio is unsigned. */
  unsigned: boolean;
  sampleRate: number;
  channels: number;
  bytesPerFrame: number;
}

export type AudioCodec = AacCodec | Mp3Codec | PcmCodec;

/** Compressed audio: one entry per packet (an AAC or MP3 frame). */
export interface PacketTable {
  kind: 'packets';
  count: number;
  /** Decode time of each packet, in media timescale ticks. */
  times: Float64Array;
  durations: Float64Array;
  offsets: Float64Array;
  sizes: Uint32Array;
}

/** Uncompressed audio: runs of frames stored back to back. */
export interface FrameTable {
  kind: 'frames';
  count: number;
  /** Byte offset, first frame number and frame count of each chunk. */
  offsets: Float64Array;
  firstFrames: Float64Array;
  frameCounts: Float64Array;
  totalFrames: number;
}

export interface AudioTrack {
  codec: AudioCodec;
  /** Media timescale: ticks per second. */
  timescale: number;
  /** Media time (ticks) shown at presentation time `startDelay`: the edit list's media time. */
  mediaStart: number;
  /** Seconds before the first sound is shown (an initial empty edit). */
  startDelay: number;
  table: PacketTable | FrameTable;
  fragmented: boolean;
  /** Presentation seconds the indexed samples cover (for a partial read of a fragmented file, only what was read). */
  durationSeconds: number;
  /** For a fragmented file read only partly: true when the end of the file was reached. */
  complete: boolean;
}

// ---------------------------------------------------------------------------
// Low-level helpers
// ---------------------------------------------------------------------------

const TWO_POW_32 = 2 ** 32;
/** Top-level box reads come in blocks this big, so a fragment costs one read. */
const SCAN_BLOCK_BYTES = 64 * 1024;
/** A movie box larger than this is not a real file's index. */
const MAX_MOOV_BYTES = 256 * 1024 * 1024;
const MAX_MOOF_BYTES = 32 * 1024 * 1024;
const MAX_TOP_LEVEL_BOXES = 200_000;

/** File types an ISO BMFF / QuickTime file can start with. */
const LEADING_BOX_TYPES = new Set(['ftyp', 'moov', 'mdat', 'free', 'skip', 'wide', 'pnot', 'uuid', 'styp', 'sidx', 'moof']);

interface BoxRef {
  type: string;
  /** Offset of the box within `data`. */
  start: number;
  payload: number;
  end: number;
}

const fourCC = (data: Uint8Array, offset: number): string | null => {
  if (offset + 4 > data.length) return null;
  let text = '';
  for (let i = 0; i < 4; i += 1) {
    const code = data[offset + i];
    if ((code < 0x20 || code > 0x7e) && code !== 0xa9) return null;
    text += String.fromCharCode(code);
  }
  return text;
};

const u16 = (data: Uint8Array, offset: number) => (data[offset] << 8) | data[offset + 1];
const u32 = (data: Uint8Array, offset: number) =>
  ((data[offset] << 24) >>> 0) + ((data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3]);
const i32 = (data: Uint8Array, offset: number) => u32(data, offset) | 0;
const u64 = (data: Uint8Array, offset: number) => u32(data, offset) * TWO_POW_32 + u32(data, offset + 4);
/** Signed 64-bit; only -1 and safe positive values occur in practice. */
const i64 = (data: Uint8Array, offset: number) => {
  const high = i32(data, offset);
  const low = u32(data, offset + 4);
  return high * TWO_POW_32 + low;
};

const corrupt = (what: string) => new MediaReadError('corrupt', `The file's ${what} could not be read.`);

const need = (data: Uint8Array, end: number, what: string) => {
  if (end > data.length) throw corrupt(what);
};

/** Boxes directly inside `data[start, end)`. */
const childBoxes = (data: Uint8Array, start: number, end: number): BoxRef[] => {
  const boxes: BoxRef[] = [];
  let offset = start;
  while (offset + 8 <= end) {
    const size32 = u32(data, offset);
    const type = fourCC(data, offset + 4);
    if (!type) break;
    let size = size32;
    let header = 8;
    if (size32 === 1) {
      if (offset + 16 > end) break;
      size = u64(data, offset + 8);
      header = 16;
    } else if (size32 === 0) {
      size = end - offset;
    }
    if (size < header || offset + size > end) break;
    boxes.push({ type, start: offset, payload: offset + header, end: offset + size });
    offset += size;
  }
  return boxes;
};

const findBox = (data: Uint8Array, start: number, end: number, type: string): BoxRef | null =>
  childBoxes(data, start, end).find((box) => box.type === type) ?? null;

/** A box at the end of a path of nested types, e.g. ['mdia', 'minf', 'stbl']. */
const findPath = (data: Uint8Array, parent: BoxRef, path: string[]): BoxRef | null => {
  let current: BoxRef | null = parent;
  for (const type of path) {
    if (!current) return null;
    current = findBox(data, current.payload, current.end, type);
  }
  return current;
};

// ---------------------------------------------------------------------------
// Top-level scan
// ---------------------------------------------------------------------------

interface TopBox {
  type: string;
  /** Absolute file offset. */
  start: number;
  headerSize: number;
  size: number;
}

interface ScanState {
  source: ByteSource;
  signal?: AbortSignal;
  block: Uint8Array | null;
  blockStart: number;
}

const readBlock = async (state: ScanState, offset: number, minLength: number) => {
  if (
    state.block &&
    offset >= state.blockStart &&
    offset + minLength <= state.blockStart + state.block.length
  ) {
    return;
  }
  const length = Math.min(Math.max(SCAN_BLOCK_BYTES, minLength), state.source.size - offset);
  state.block = await state.source.read(offset, length, state.signal);
  state.blockStart = offset;
};

/** The top-level box at `offset`, or null at the end of the file or on garbage. */
const readTopBox = async (state: ScanState, offset: number): Promise<TopBox | null> => {
  const { size: fileSize } = state.source;
  if (offset + 8 > fileSize) return null;
  await readBlock(state, offset, Math.min(16, fileSize - offset));
  const block = state.block as Uint8Array;
  const at = offset - state.blockStart;
  const size32 = u32(block, at);
  const type = fourCC(block, at + 4);
  if (!type) return null;
  let size = size32;
  let headerSize = 8;
  if (size32 === 1) {
    if (at + 16 > block.length) return null;
    size = u64(block, at + 8);
    headerSize = 16;
  } else if (size32 === 0) {
    size = fileSize - offset;
  }
  if (size < headerSize) return null;
  // A box running past the end of the file is a truncated upload; keep what is there.
  if (offset + size > fileSize) size = fileSize - offset;
  return { type, start: offset, headerSize, size };
};

const readWholeBox = async (state: ScanState, box: TopBox, limit: number, what: string): Promise<Uint8Array> => {
  if (box.size > limit) throw corrupt(what);
  await readBlock(state, box.start, box.size);
  const block = state.block as Uint8Array;
  const at = box.start - state.blockStart;
  return block.subarray(at, at + box.size);
};

// ---------------------------------------------------------------------------
// Movie header, edit list, sample description
// ---------------------------------------------------------------------------

const readTimescale = (data: Uint8Array, box: BoxRef, what: string): number => {
  const version = data[box.payload];
  const at = box.payload + (version === 1 ? 20 : 12);
  need(data, at + 4, what);
  const timescale = u32(data, at);
  if (!(timescale > 0)) throw corrupt(what);
  return timescale;
};

const readHandler = (data: Uint8Array, hdlr: BoxRef | null): string | null =>
  hdlr && hdlr.payload + 12 <= hdlr.end ? fourCC(data, hdlr.payload + 8) : null;

interface EditTiming {
  mediaStart: number;
  startDelay: number;
}

/** The first real edit's media time, after any initial empty edits. */
const readEdits = (data: Uint8Array, trak: BoxRef, movieTimescale: number): EditTiming => {
  const elst = findPath(data, trak, ['edts', 'elst']);
  if (!elst) return { mediaStart: 0, startDelay: 0 };
  const version = data[elst.payload];
  const count = u32(data, elst.payload + 4);
  const entrySize = version === 1 ? 20 : 12;
  let startDelay = 0;
  for (let i = 0; i < count; i += 1) {
    const at = elst.payload + 8 + i * entrySize;
    if (at + entrySize > elst.end) break;
    const segmentDuration = version === 1 ? u64(data, at) : u32(data, at);
    const mediaTime = version === 1 ? i64(data, at + 8) : i32(data, at + 4);
    if (mediaTime === -1) {
      startDelay += segmentDuration / movieTimescale;
      continue;
    }
    return { mediaStart: Math.max(0, mediaTime), startDelay };
  }
  return { mediaStart: 0, startDelay };
};

const AAC_SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

/** A reader over a bit string, for the AudioSpecificConfig. */
class BitReader {
  private bit = 0;

  constructor(private readonly bytes: Uint8Array) {}

  read(count: number): number {
    let value = 0;
    for (let i = 0; i < count; i += 1) {
      const byte = this.bytes[this.bit >> 3];
      if (byte === undefined) throw corrupt('audio settings');
      value = value * 2 + ((byte >> (7 - (this.bit & 7))) & 1);
      this.bit += 1;
    }
    return value;
  }
}

/** The AAC settings ADTS headers need, from an AudioSpecificConfig. */
export const parseAudioSpecificConfig = (config: Uint8Array): AacCodec => {
  const bits = new BitReader(config);
  const objectTypeOf = () => {
    const type = bits.read(5);
    return type === 31 ? 32 + bits.read(6) : type;
  };
  const rateOf = (): { index: number; rate: number } => {
    const index = bits.read(4);
    if (index === 15) return { index, rate: bits.read(24) };
    return { index, rate: AAC_SAMPLE_RATES[index] ?? 0 };
  };

  let objectType = objectTypeOf();
  let { index, rate } = rateOf();
  const channelConfig = bits.read(4);
  // Explicitly signalled HE-AAC (SBR, PS): the core underneath is what ADTS carries.
  if (objectType === 5 || objectType === 29) {
    rateOf(); // the extension (output) rate
    objectType = objectTypeOf();
  }
  if (objectType < 1 || objectType > 4) {
    throw new MediaReadError('unsupported-audio', 'This AAC variant is not supported.');
  }
  if (index === 15 || !(rate > 0)) {
    throw new MediaReadError('unsupported-audio', 'This AAC sample rate is not supported.');
  }
  if (channelConfig < 1 || channelConfig > 7) {
    throw new MediaReadError('unsupported-audio', 'This AAC channel layout is not supported.');
  }
  const channels = channelConfig === 7 ? 8 : channelConfig;
  return { kind: 'aac', objectType, samplingIndex: index, channelConfig, sampleRate: rate, channels };
};

/** Descriptor length: up to four bytes, seven bits each. */
const descriptorLength = (data: Uint8Array, at: number, end: number): { length: number; next: number } => {
  let length = 0;
  let next = at;
  for (let i = 0; i < 4 && next < end; i += 1) {
    const byte = data[next];
    next += 1;
    length = length * 128 + (byte & 0x7f);
    if (!(byte & 0x80)) break;
  }
  return { length, next };
};

interface EsdsInfo {
  objectTypeIndication: number;
  config: Uint8Array | null;
}

/** The decoder config of an `esds` box: object type and AudioSpecificConfig. */
const readEsds = (data: Uint8Array, esds: BoxRef): EsdsInfo | null => {
  let at = esds.payload + 4; // version and flags
  const end = esds.end;
  if (data[at] !== 0x03) return null;
  let { next } = descriptorLength(data, at + 1, end);
  at = next + 2; // ES_ID
  const flags = data[at];
  at += 1;
  if (flags & 0x80) at += 2; // dependsOn_ES_ID
  if (flags & 0x40) at += 1 + data[at]; // URL
  if (flags & 0x20) at += 2; // OCR_ES_ID
  if (data[at] !== 0x04) return null;
  ({ next } = descriptorLength(data, at + 1, end));
  at = next;
  const objectTypeIndication = data[at];
  at += 13; // objectType, streamType, bufferSizeDB, maxBitrate, avgBitrate
  let config: Uint8Array | null = null;
  if (at < end && data[at] === 0x05) {
    const info = descriptorLength(data, at + 1, end);
    if (info.next + info.length <= end) config = data.slice(info.next, info.next + info.length);
  }
  return { objectTypeIndication, config };
};

/** Searches the children of a sample entry, looking inside a QuickTime `wave` box too. */
const findEntryChild = (data: Uint8Array, start: number, end: number, type: string): BoxRef | null => {
  const children = childBoxes(data, start, end);
  const direct = children.find((box) => box.type === type);
  if (direct) return direct;
  const wave = children.find((box) => box.type === 'wave');
  return wave ? findBox(data, wave.payload, wave.end, type) : null;
};

const KNOWN_ENTRY_CHILDREN = new Set(['esds', 'wave', 'chan', 'srat', 'btrt', 'dOps', 'pcmC', 'alac', 'dac3', 'dec3', 'sinf', 'enda']);

/**
 * Where a sound sample entry's child boxes start. QuickTime version 1 entries
 * have 16 extra bytes and version 2 entries 36 more; ISO's own version 1
 * entries have none, so for version 1 the box that follows is checked.
 */
const entryChildrenStart = (data: Uint8Array, entry: BoxRef, version: number): number => {
  const base = entry.start + 36;
  if (version === 2) return base + 36;
  if (version === 1) {
    const isoChild = fourCC(data, base + 4);
    if (isoChild && KNOWN_ENTRY_CHILDREN.has(isoChild) && u32(data, base) >= 8) return base;
    return base + 16;
  }
  return base;
};

const PCM_FORMATS: Record<string, { bits?: PcmCodec['bits']; float?: boolean; littleEndian: boolean; unsigned?: boolean }> = {
  twos: { littleEndian: false },
  sowt: { littleEndian: true },
  in24: { bits: 24, littleEndian: false },
  in32: { bits: 32, littleEndian: false },
  fl32: { bits: 32, float: true, littleEndian: false },
  fl64: { bits: 64, float: true, littleEndian: false },
  'raw ': { bits: 8, littleEndian: false, unsigned: true },
  NONE: { littleEndian: false },
};

const normalizeBits = (bits: number): PcmCodec['bits'] | null =>
  bits === 8 || bits === 16 || bits === 24 || bits === 32 || bits === 64 ? bits : null;

/** The codec of the first sample entry of an audio track's `stsd`. */
const readAudioCodec = (data: Uint8Array, stsd: BoxRef, mediaTimescale: number): AudioCodec => {
  const entry = childBoxes(data, stsd.payload + 8, stsd.end)[0];
  if (!entry) throw corrupt('audio description');
  need(data, entry.start + 36, 'audio description');
  const format = entry.type;
  const version = u16(data, entry.start + 16);
  const entryChannels = u16(data, entry.start + 24);
  const entryBits = u16(data, entry.start + 26);
  const fixedRate = u32(data, entry.start + 32) / 65536;
  const childrenStart = Math.min(entry.end, entryChildrenStart(data, entry, version));

  let sampleRate = fixedRate > 0 ? fixedRate : mediaTimescale;
  let channels = entryChannels;
  let bytesPerFrameV1 = 0;
  if (version === 1 && childrenStart === entry.start + 52) {
    bytesPerFrameV1 = u32(data, entry.start + 44);
  }
  if (version === 2) {
    need(data, entry.start + 72, 'audio description');
    const view = new DataView(data.buffer, data.byteOffset + entry.start + 40, 8);
    sampleRate = view.getFloat64(0);
    channels = u32(data, entry.start + 48);
  }
  // A 16.16 rate cannot say 88200 or 96000; the media timescale usually does.
  if (sampleRate < 8000 && mediaTimescale >= 8000) sampleRate = mediaTimescale;

  if (format === 'mp4a') {
    const esdsBox = findEntryChild(data, childrenStart, entry.end, 'esds');
    const esds = esdsBox ? readEsds(data, esdsBox) : null;
    if (esds && (esds.objectTypeIndication === 0x69 || esds.objectTypeIndication === 0x6b)) {
      return { kind: 'mp3', sampleRate, channels: Math.max(1, channels) };
    }
    if (esds?.config && esds.config.length >= 2) {
      const codec = parseAudioSpecificConfig(esds.config);
      return { ...codec, channels: codec.channels || Math.max(1, channels) };
    }
    // MPEG-2 AAC without a config: derive it from the entry (AAC-LC).
    const index = AAC_SAMPLE_RATES.indexOf(Math.round(sampleRate));
    if (index < 0 || channels < 1 || channels > 6) {
      throw new MediaReadError('unsupported-audio', 'This AAC track has no usable settings.');
    }
    return { kind: 'aac', objectType: 2, samplingIndex: index, channelConfig: channels, sampleRate, channels };
  }
  if (format === '.mp3' || format === 'mp3 ') {
    return { kind: 'mp3', sampleRate, channels: Math.max(1, channels) };
  }

  let pcm: { bits: number; float: boolean; littleEndian: boolean; unsigned: boolean } | null = null;
  if (format in PCM_FORMATS) {
    const known = PCM_FORMATS[format];
    const enda = findEntryChild(data, childrenStart, entry.end, 'enda');
    const littleEndian = enda ? u16(data, enda.payload) === 1 || known.littleEndian : known.littleEndian;
    const bits = known.bits ?? (version === 1 && bytesPerFrameV1 > 0 && channels > 0 ? (bytesPerFrameV1 / channels) * 8 : entryBits);
    pcm = { bits, float: Boolean(known.float), littleEndian, unsigned: Boolean(known.unsigned) };
  } else if (format === 'lpcm' && version === 2) {
    const bits = u32(data, entry.start + 56);
    const flags = u32(data, entry.start + 60);
    pcm = { bits, float: Boolean(flags & 1), littleEndian: !(flags & 2), unsigned: !(flags & 1) && !(flags & 4) };
  } else if (format === 'ipcm' || format === 'fpcm') {
    const pcmC = findEntryChild(data, childrenStart, entry.end, 'pcmC');
    if (pcmC && pcmC.payload + 6 <= pcmC.end) {
      const formatFlags = data[pcmC.payload + 4];
      const bits = data[pcmC.payload + 5];
      pcm = { bits, float: format === 'fpcm', littleEndian: Boolean(formatFlags & 1), unsigned: false };
    }
  }
  if (pcm) {
    const bits = normalizeBits(pcm.bits);
    if (!bits || (pcm.float && bits !== 32 && bits !== 64) || channels < 1) {
      throw new MediaReadError('unsupported-audio', 'This PCM audio format is not supported.');
    }
    return {
      kind: 'pcm',
      bits,
      float: pcm.float,
      littleEndian: pcm.littleEndian,
      unsigned: pcm.unsigned,
      sampleRate,
      channels,
      bytesPerFrame: (channels * bits) / 8,
    };
  }
  throw new MediaReadError('unsupported-audio', `Audio in the "${format.trim()}" format is not supported.`);
};

// ---------------------------------------------------------------------------
// Sample tables (ordinary files)
// ---------------------------------------------------------------------------

interface SampleSizes {
  count: number;
  constant: number;
  sizes: Uint32Array | null;
}

const readSampleSizes = (data: Uint8Array, stbl: BoxRef): SampleSizes => {
  const stsz = findBox(data, stbl.payload, stbl.end, 'stsz');
  if (stsz) {
    const constant = u32(data, stsz.payload + 4);
    const count = u32(data, stsz.payload + 8);
    if (constant !== 0) return { count, constant, sizes: null };
    need(data, stsz.payload + 12 + count * 4, 'sample sizes');
    const sizes = new Uint32Array(count);
    for (let i = 0; i < count; i += 1) sizes[i] = u32(data, stsz.payload + 12 + i * 4);
    return { count, constant: 0, sizes };
  }
  const stz2 = findBox(data, stbl.payload, stbl.end, 'stz2');
  if (!stz2) throw corrupt('sample sizes');
  const fieldSize = data[stz2.payload + 7];
  const count = u32(data, stz2.payload + 8);
  const sizes = new Uint32Array(count);
  const base = stz2.payload + 12;
  need(data, base + Math.ceil((count * fieldSize) / 8), 'sample sizes');
  for (let i = 0; i < count; i += 1) {
    if (fieldSize === 16) sizes[i] = u16(data, base + i * 2);
    else if (fieldSize === 8) sizes[i] = data[base + i];
    else if (fieldSize === 4) sizes[i] = (data[base + (i >> 1)] >> (i & 1 ? 0 : 4)) & 0xf;
    else throw corrupt('sample sizes');
  }
  return { count, constant: 0, sizes };
};

const readChunkOffsets = (data: Uint8Array, stbl: BoxRef): Float64Array => {
  const stco = findBox(data, stbl.payload, stbl.end, 'stco');
  if (stco) {
    const count = u32(data, stco.payload + 4);
    need(data, stco.payload + 8 + count * 4, 'chunk offsets');
    const offsets = new Float64Array(count);
    for (let i = 0; i < count; i += 1) offsets[i] = u32(data, stco.payload + 8 + i * 4);
    return offsets;
  }
  const co64 = findBox(data, stbl.payload, stbl.end, 'co64');
  if (!co64) throw corrupt('chunk offsets');
  const count = u32(data, co64.payload + 4);
  need(data, co64.payload + 8 + count * 8, 'chunk offsets');
  const offsets = new Float64Array(count);
  for (let i = 0; i < count; i += 1) offsets[i] = u64(data, co64.payload + 8 + i * 8);
  return offsets;
};

/** Samples in each chunk, from the sample-to-chunk runs. */
const readSamplesPerChunk = (data: Uint8Array, stbl: BoxRef, chunkCount: number): Float64Array => {
  const stsc = findBox(data, stbl.payload, stbl.end, 'stsc');
  if (!stsc) throw corrupt('sample-to-chunk table');
  const entries = u32(data, stsc.payload + 4);
  need(data, stsc.payload + 8 + entries * 12, 'sample-to-chunk table');
  const perChunk = new Float64Array(chunkCount);
  for (let e = 0; e < entries; e += 1) {
    const at = stsc.payload + 8 + e * 12;
    const firstChunk = u32(data, at);
    const samples = u32(data, at + 4);
    const nextFirst = e + 1 < entries ? u32(data, at + 12) : chunkCount + 1;
    for (let chunk = Math.max(1, firstChunk); chunk < nextFirst && chunk <= chunkCount; chunk += 1) {
      perChunk[chunk - 1] = samples;
    }
  }
  return perChunk;
};

/** Decode-time deltas, expanded per sample. */
const readSampleDurations = (data: Uint8Array, stbl: BoxRef, count: number): Float64Array => {
  const stts = findBox(data, stbl.payload, stbl.end, 'stts');
  if (!stts) throw corrupt('sample timing');
  const entries = u32(data, stts.payload + 4);
  need(data, stts.payload + 8 + entries * 8, 'sample timing');
  const durations = new Float64Array(count);
  let sample = 0;
  let lastDelta = 0;
  for (let e = 0; e < entries && sample < count; e += 1) {
    const runCount = u32(data, stts.payload + 8 + e * 8);
    const delta = u32(data, stts.payload + 12 + e * 8);
    lastDelta = delta;
    for (let i = 0; i < runCount && sample < count; i += 1) {
      durations[sample] = delta;
      sample += 1;
    }
  }
  for (; sample < count; sample += 1) durations[sample] = lastDelta;
  return durations;
};

const buildPacketTable = (data: Uint8Array, stbl: BoxRef): PacketTable => {
  const sizes = readSampleSizes(data, stbl);
  const chunkOffsets = readChunkOffsets(data, stbl);
  const perChunk = readSamplesPerChunk(data, stbl, chunkOffsets.length);
  const count = sizes.count;
  const durations = readSampleDurations(data, stbl, count);
  const times = new Float64Array(count);
  const offsets = new Float64Array(count);
  const packetSizes = new Uint32Array(count);
  let sample = 0;
  for (let chunk = 0; chunk < chunkOffsets.length && sample < count; chunk += 1) {
    let offset = chunkOffsets[chunk];
    for (let i = 0; i < perChunk[chunk] && sample < count; i += 1) {
      const size = sizes.sizes ? sizes.sizes[sample] : sizes.constant;
      offsets[sample] = offset;
      packetSizes[sample] = size;
      offset += size;
      sample += 1;
    }
  }
  let time = 0;
  for (let i = 0; i < sample; i += 1) {
    times[i] = time;
    time += durations[i];
  }
  return {
    kind: 'packets',
    count: sample,
    times: times.subarray(0, sample),
    durations: durations.subarray(0, sample),
    offsets: offsets.subarray(0, sample),
    sizes: packetSizes.subarray(0, sample),
  };
};

/**
 * PCM chunks. The frame count comes from the sample-to-chunk table and the
 * byte size from the codec: QuickTime files store PCM with a sample size of
 * 1 or of one frame, and either way a "sample" is one frame.
 */
const buildFrameTable = (data: Uint8Array, stbl: BoxRef): FrameTable => {
  const chunkOffsets = readChunkOffsets(data, stbl);
  const perChunk = readSamplesPerChunk(data, stbl, chunkOffsets.length);
  const sizes = readSampleSizes(data, stbl);
  const firstFrames = new Float64Array(chunkOffsets.length);
  let total = 0;
  for (let chunk = 0; chunk < chunkOffsets.length; chunk += 1) {
    firstFrames[chunk] = total;
    total += perChunk[chunk];
  }
  // stsz counts frames; trust it when the chunk runs overshoot (padding chunks).
  const totalFrames = sizes.count > 0 ? Math.min(total, sizes.count) : total;
  return {
    kind: 'frames',
    count: chunkOffsets.length,
    offsets: chunkOffsets,
    firstFrames,
    frameCounts: perChunk,
    totalFrames,
  };
};

// ---------------------------------------------------------------------------
// Fragmented files
// ---------------------------------------------------------------------------

interface TrackDefaults {
  duration: number;
  size: number;
}

class GrowablePackets {
  count = 0;
  times = new Float64Array(1024);
  durations = new Float64Array(1024);
  offsets = new Float64Array(1024);
  sizes = new Uint32Array(1024);

  push(time: number, duration: number, offset: number, size: number) {
    if (this.count === this.times.length) {
      const grow = <T extends Float64Array | Uint32Array>(array: T): T => {
        const next = new (array.constructor as { new (length: number): T })(array.length * 2);
        next.set(array);
        return next;
      };
      this.times = grow(this.times);
      this.durations = grow(this.durations);
      this.offsets = grow(this.offsets);
      this.sizes = grow(this.sizes);
    }
    this.times[this.count] = time;
    this.durations[this.count] = duration;
    this.offsets[this.count] = offset;
    this.sizes[this.count] = size;
    this.count += 1;
  }

  table(): PacketTable {
    return {
      kind: 'packets',
      count: this.count,
      times: this.times.slice(0, this.count),
      durations: this.durations.slice(0, this.count),
      offsets: this.offsets.slice(0, this.count),
      sizes: this.sizes.slice(0, this.count),
    };
  }
}

/** Appends the audio track's samples from one `moof` box. */
const readFragment = (
  moof: Uint8Array,
  moofStart: number,
  trackId: number,
  defaults: TrackDefaults,
  packets: GrowablePackets,
  nextTime: number
): number => {
  let time = nextTime;
  const root = childBoxes(moof, 0, moof.length)[0];
  if (!root) return time;
  for (const traf of childBoxes(moof, root.payload, root.end).filter((box) => box.type === 'traf')) {
    const children = childBoxes(moof, traf.payload, traf.end);
    const tfhd = children.find((box) => box.type === 'tfhd');
    if (!tfhd || u32(moof, tfhd.payload + 4) !== trackId) continue;
    const tfhdFlags = u32(moof, tfhd.payload) & 0xffffff;
    let at = tfhd.payload + 8;
    let baseOffset = moofStart;
    if (tfhdFlags & 0x1) {
      baseOffset = u64(moof, at);
      at += 8;
    }
    if (tfhdFlags & 0x2) at += 4;
    let defaultDuration = defaults.duration;
    let defaultSize = defaults.size;
    if (tfhdFlags & 0x8) {
      defaultDuration = u32(moof, at);
      at += 4;
    }
    if (tfhdFlags & 0x10) {
      defaultSize = u32(moof, at);
      at += 4;
    }

    const tfdt = children.find((box) => box.type === 'tfdt');
    if (tfdt) time = moof[tfdt.payload] === 1 ? u64(moof, tfdt.payload + 4) : u32(moof, tfdt.payload + 4);

    let dataCursor = baseOffset;
    for (const trun of children.filter((box) => box.type === 'trun')) {
      const flags = u32(moof, trun.payload) & 0xffffff;
      const sampleCount = u32(moof, trun.payload + 4);
      let cursor = trun.payload + 8;
      if (flags & 0x1) {
        dataCursor = baseOffset + i32(moof, cursor);
        cursor += 4;
      }
      if (flags & 0x4) cursor += 4;
      const perSample =
        (flags & 0x100 ? 4 : 0) + (flags & 0x200 ? 4 : 0) + (flags & 0x400 ? 4 : 0) + (flags & 0x800 ? 4 : 0);
      need(moof, cursor + sampleCount * perSample, 'fragment');
      for (let i = 0; i < sampleCount; i += 1) {
        let duration = defaultDuration;
        let size = defaultSize;
        if (flags & 0x100) {
          duration = u32(moof, cursor);
          cursor += 4;
        }
        if (flags & 0x200) {
          size = u32(moof, cursor);
          cursor += 4;
        }
        if (flags & 0x400) cursor += 4;
        if (flags & 0x800) cursor += 4;
        packets.push(time, duration, dataCursor, size);
        time += duration;
        dataCursor += size;
      }
    }
  }
  return time;
};

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export interface ReadAudioTrackOptions {
  signal?: AbortSignal;
  /**
   * Fragmented files only: stop reading fragments once this many seconds of
   * audio are indexed (an ordinary file's index is read whole either way).
   */
  maxSeconds?: number;
}

interface AudioTrakInfo {
  trackId: number;
  timescale: number;
  codec: AudioCodec;
  edits: EditTiming;
  stbl: BoxRef;
}

const findAudioTrak = (moov: Uint8Array): AudioTrakInfo | null => {
  const root = childBoxes(moov, 0, moov.length)[0];
  if (!root || root.type !== 'moov') throw corrupt('movie index');
  const mvhd = findBox(moov, root.payload, root.end, 'mvhd');
  const movieTimescale = mvhd ? readTimescale(moov, mvhd, 'movie header') : 1000;
  const traks = childBoxes(moov, root.payload, root.end).filter((box) => box.type === 'trak');
  // Prefer an enabled track; cameras sometimes add disabled extra channels.
  const ordered = traks
    .map((trak) => {
      const tkhd = findBox(moov, trak.payload, trak.end, 'tkhd');
      const enabled = tkhd ? (u32(moov, tkhd.payload) & 1) === 1 : true;
      return { trak, enabled, tkhd };
    })
    .sort((a, b) => Number(b.enabled) - Number(a.enabled));
  for (const { trak, tkhd } of ordered) {
    const mdia = findBox(moov, trak.payload, trak.end, 'mdia');
    if (!mdia) continue;
    if (readHandler(moov, findBox(moov, mdia.payload, mdia.end, 'hdlr')) !== 'soun') continue;
    const mdhd = findBox(moov, mdia.payload, mdia.end, 'mdhd');
    const stbl = findPath(moov, mdia, ['minf', 'stbl']);
    const stsd = stbl ? findBox(moov, stbl.payload, stbl.end, 'stsd') : null;
    if (!mdhd || !stbl || !stsd) continue;
    const timescale = readTimescale(moov, mdhd, 'audio header');
    const version = tkhd ? moov[tkhd.payload] : 0;
    const trackId = tkhd ? u32(moov, tkhd.payload + (version === 1 ? 20 : 12)) : 0;
    return {
      trackId,
      timescale,
      codec: readAudioCodec(moov, stsd, timescale),
      edits: readEdits(moov, trak, movieTimescale),
      stbl,
    };
  }
  return null;
};

const readTrackDefaults = (moov: Uint8Array, trackId: number): TrackDefaults => {
  const root = childBoxes(moov, 0, moov.length)[0];
  const mvex = root ? findBox(moov, root.payload, root.end, 'mvex') : null;
  const trex = mvex
    ? childBoxes(moov, mvex.payload, mvex.end).find((box) => box.type === 'trex' && u32(moov, box.payload + 4) === trackId)
    : null;
  return trex
    ? { duration: u32(moov, trex.payload + 12), size: u32(moov, trex.payload + 16) }
    : { duration: 0, size: 0 };
};

const presentationSeconds = (track: Pick<AudioTrack, 'timescale' | 'mediaStart' | 'startDelay'>, ticks: number) =>
  (ticks - track.mediaStart) / track.timescale + track.startDelay;

/**
 * Reads the audio track's index. Throws MediaReadError for files that are
 * not MP4/MOV, have no audio, or use an audio format that can't be decoded.
 */
export const readAudioTrack = async (source: ByteSource, options: ReadAudioTrackOptions = {}): Promise<AudioTrack> => {
  const state: ScanState = { source, signal: options.signal, block: null, blockStart: 0 };
  let offset = 0;
  let moov: Uint8Array | null = null;
  let moovEnd = 0;
  let boxes = 0;
  while (!moov) {
    const box = await readTopBox(state, offset);
    if (!box) break;
    if (boxes === 0 && !LEADING_BOX_TYPES.has(box.type)) {
      throw new MediaReadError('not-mp4', 'Only MP4 and MOV files can be read this way.');
    }
    boxes += 1;
    if (boxes > MAX_TOP_LEVEL_BOXES) break;
    if (box.type === 'moov') {
      moov = (await readWholeBox(state, box, MAX_MOOV_BYTES, 'movie index')).slice();
      moovEnd = box.start + box.size;
    }
    offset = box.start + box.size;
  }
  if (!moov) {
    if (boxes === 0) throw new MediaReadError('not-mp4', 'Only MP4 and MOV files can be read this way.');
    throw corrupt('movie index');
  }

  const info = findAudioTrak(moov);
  if (!info) throw new MediaReadError('no-audio', 'This video has no sound track.');
  const { codec, timescale, edits } = info;
  const root = childBoxes(moov, 0, moov.length)[0];
  const fragmented = Boolean(root && findBox(moov, root.payload, root.end, 'mvex'));

  const base = { codec, timescale, mediaStart: edits.mediaStart, startDelay: edits.startDelay, fragmented };

  if (!fragmented) {
    if (codec.kind === 'pcm') {
      const table = buildFrameTable(moov, info.stbl);
      if (table.totalFrames === 0) throw new MediaReadError('no-audio', 'This video has no sound.');
      const framesPerTick = codec.sampleRate / timescale;
      const endSeconds = table.totalFrames / codec.sampleRate - edits.mediaStart / framesPerTick / codec.sampleRate;
      return { ...base, table, durationSeconds: Math.max(0, endSeconds + edits.startDelay), complete: true };
    }
    const table = buildPacketTable(moov, info.stbl);
    if (table.count === 0) throw new MediaReadError('no-audio', 'This video has no sound.');
    const last = table.count - 1;
    const end = presentationSeconds(base, table.times[last] + table.durations[last]);
    return { ...base, table, durationSeconds: Math.max(0, end), complete: true };
  }

  // Fragmented: the samples are described by the moof boxes after the moov.
  if (codec.kind === 'pcm') {
    throw new MediaReadError('unsupported-audio', 'PCM audio in fragmented files is not supported.');
  }
  const packets = new GrowablePackets();
  // Samples the moov itself lists (some fragmented files start with a few).
  const initial = buildPacketTable(moov, info.stbl);
  for (let i = 0; i < initial.count; i += 1) {
    packets.push(initial.times[i], initial.durations[i], initial.offsets[i], initial.sizes[i]);
  }
  let nextTime = initial.count ? initial.times[initial.count - 1] + initial.durations[initial.count - 1] : 0;
  const defaults = readTrackDefaults(moov, info.trackId);
  const maxTicks = options.maxSeconds ? edits.mediaStart + (options.maxSeconds - edits.startDelay) * timescale : Infinity;
  offset = moovEnd;
  let complete = true;
  for (let count = 0; count < MAX_TOP_LEVEL_BOXES; count += 1) {
    if (nextTime > maxTicks) {
      complete = false;
      break;
    }
    const box = await readTopBox(state, offset);
    if (!box) break;
    if (box.type === 'moof') {
      const moof = await readWholeBox(state, box, MAX_MOOF_BYTES, 'fragment');
      nextTime = readFragment(moof, box.start, info.trackId, defaults, packets, nextTime);
    }
    offset = box.start + box.size;
  }
  const table = packets.table();
  if (table.count === 0) throw new MediaReadError('no-audio', 'This video has no sound.');
  const last = table.count - 1;
  const end = presentationSeconds(base, table.times[last] + table.durations[last]);
  return { ...base, table, durationSeconds: Math.max(0, end), complete };
};

// ---------------------------------------------------------------------------
// Planning reads for a stretch of time
// ---------------------------------------------------------------------------

/** Presentation time (s) of a packet's start. */
export const packetTime = (track: AudioTrack, index: number): number => {
  const table = track.table as PacketTable;
  return presentationSeconds(track, table.times[index]);
};

/** Index of the first packet whose decode time is at or after `ticks`. */
const lowerBound = (values: Float64Array, count: number, target: number): number => {
  let low = 0;
  let high = count;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (values[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low;
};

/**
 * Packets covering presentation seconds [startS, endS), plus `warmUp`
 * packets before (an AAC decoder needs the frame before to rebuild the first
 * one cleanly). Returns [first, endExclusive).
 */
export const packetRange = (track: AudioTrack, startS: number, endS: number, warmUp = 2): [number, number] => {
  const table = track.table as PacketTable;
  const toTicks = (seconds: number) => (seconds - track.startDelay) * track.timescale + track.mediaStart;
  let first = lowerBound(table.times, table.count, toTicks(startS));
  // The packet that contains startS begins before it.
  if (first > 0) first -= 1;
  first = Math.max(0, first - warmUp);
  const last = Math.min(table.count, lowerBound(table.times, table.count, toTicks(endS)) + 1);
  return [first, Math.max(first, last)];
};

export interface ByteRun {
  offset: number;
  length: number;
}

/**
 * Merges the byte ranges of packets [first, end) into reads: back-to-back
 * packets always, and nearby ones when the bytes skipped between them are
 * fewer than `maxGap` (cheaper than another request).
 */
export const planPacketReads = (table: PacketTable, first: number, end: number, maxGap = 16 * 1024): ByteRun[] => {
  const pieces: ByteRun[] = [];
  for (let i = first; i < end; i += 1) {
    if (table.sizes[i] > 0) pieces.push({ offset: table.offsets[i], length: table.sizes[i] });
  }
  return coalesceRuns(pieces, maxGap);
};

/**
 * Merges byte ranges (sorted by offset) into reads, joining ranges separated
 * by at most `maxGap` bytes: reading a little video in between is cheaper
 * than another request.
 */
export const coalesceRuns = (pieces: ByteRun[], maxGap: number): ByteRun[] => {
  const sorted = pieces.filter((piece) => piece.length > 0).sort((a, b) => a.offset - b.offset);
  const runs: ByteRun[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const piece = sorted[i];
    const last = runs[runs.length - 1];
    if (last && piece.offset - (last.offset + last.length) <= maxGap) {
      last.length = Math.max(last.length, piece.offset + piece.length - last.offset);
    } else {
      runs.push({ offset: piece.offset, length: piece.length });
    }
  }
  return runs;
};

/** The bytes [offset, offset + length) out of reads made for `runs`. */
export const sliceFromRuns = (runs: ByteRun[], data: Uint8Array[], offset: number, length: number): Uint8Array => {
  let low = 0;
  let high = runs.length - 1;
  while (low < high) {
    const middle = (low + high + 1) >> 1;
    if (runs[middle].offset <= offset) low = middle;
    else high = middle - 1;
  }
  const run = runs[low];
  const bytes = data[low];
  if (!run || !bytes || offset < run.offset || offset + length > run.offset + bytes.length) throw corrupt('audio data');
  return bytes.subarray(offset - run.offset, offset - run.offset + length);
};

/** Reads byte runs with a few requests in flight; resolves in order. */
export const readRuns = async (
  source: ByteSource,
  runs: ByteRun[],
  {
    concurrency = 6,
    signal,
    onProgress,
  }: { concurrency?: number; signal?: AbortSignal; onProgress?: (done: number, total: number) => void } = {}
): Promise<Uint8Array[]> => {
  const results: Uint8Array[] = new Array(runs.length);
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < runs.length) {
      const index = next;
      next += 1;
      results[index] = await source.read(runs[index].offset, runs[index].length, signal);
      done += 1;
      onProgress?.(done, runs.length);
    }
  };
  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(concurrency, runs.length); i += 1) workers.push(worker());
  await Promise.all(workers);
  return results;
};

/** The bytes of packets [first, end), each as its own array. */
export const readPackets = async (
  source: ByteSource,
  track: AudioTrack,
  first: number,
  end: number,
  options: { concurrency?: number; signal?: AbortSignal; onProgress?: (done: number, total: number) => void } = {}
): Promise<Uint8Array[]> => {
  const table = track.table as PacketTable;
  const runs = planPacketReads(table, first, end);
  const data = await readRuns(source, runs, options);
  const packets: Uint8Array[] = [];
  for (let i = first; i < end; i += 1) {
    const size = table.sizes[i];
    if (size === 0) continue;
    packets.push(sliceFromRuns(runs, data, table.offsets[i], size));
  }
  return packets;
};

/** For PCM: the chunk reads covering frames [firstFrame, endFrame). */
export const planFrameReads = (
  table: FrameTable,
  bytesPerFrame: number,
  firstFrame: number,
  endFrame: number
): Array<ByteRun & { firstFrame: number; frames: number }> => {
  const reads: Array<ByteRun & { firstFrame: number; frames: number }> = [];
  for (let chunk = 0; chunk < table.count; chunk += 1) {
    const chunkStart = table.firstFrames[chunk];
    const chunkEnd = chunkStart + table.frameCounts[chunk];
    if (chunkEnd <= firstFrame) continue;
    if (chunkStart >= endFrame) break;
    const from = Math.max(firstFrame, chunkStart);
    const to = Math.min(endFrame, chunkEnd, table.totalFrames);
    if (to <= from) continue;
    const offset = table.offsets[chunk] + (from - chunkStart) * bytesPerFrame;
    const last = reads[reads.length - 1];
    if (last && last.offset + last.length === offset && last.firstFrame + last.frames === from) {
      last.length += (to - from) * bytesPerFrame;
      last.frames += to - from;
    } else {
      reads.push({ offset, length: (to - from) * bytesPerFrame, firstFrame: from, frames: to - from });
    }
  }
  return reads;
};

/** Presentation seconds of PCM frame `frame`. */
export const frameTime = (track: AudioTrack, frame: number): number => {
  const codec = track.codec as PcmCodec;
  const mediaStartFrames = (track.mediaStart * codec.sampleRate) / track.timescale;
  return (frame - mediaStartFrames) / codec.sampleRate + track.startDelay;
};

/** The PCM frame shown at presentation second `seconds` (may be negative or past the end). */
export const frameAt = (track: AudioTrack, seconds: number): number => {
  const codec = track.codec as PcmCodec;
  const mediaStartFrames = (track.mediaStart * codec.sampleRate) / track.timescale;
  return Math.floor((seconds - track.startDelay) * codec.sampleRate + mediaStartFrames);
};
